import React, { useEffect, useState } from "react";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Lock, FileText, UserPlus } from "lucide-react";
import RegisterDialog from "@/components/RegisterDialog";

export default function Catalog() {
  const { t, user, login } = useApp();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [regOpen, setRegOpen] = useState(false);

  const load = () => api.get("/catalogs").then((r) => setItems(r.data)).catch(() => setItems(null));

  useEffect(() => { if (user) load(); else setItems(null); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      login(data.token, data.user);
    } catch (er) { setError(errMsg(er)); }
    setBusy(false);
  };

  if (!user) {
    return (
      <div data-testid="catalog-gate" className="px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-6">
            <p className="overline">Private Access</p>
            <h1 className="font-serif text-4xl sm:text-6xl text-white mt-4">{t("catalogGate")}</h1>
            <div className="gold-line mt-8 w-40" />
            <p className="mt-10 text-base text-neutral-400 leading-relaxed max-w-md">{t("catalogIntro")}</p>
            <button data-testid="catalog-register-btn" onClick={() => setRegOpen(true)}
              className="mt-8 inline-flex items-center gap-2 border border-white/20 px-7 py-3 text-[0.7rem] uppercase tracking-[0.22em] text-neutral-200 hover:bg-white/10 hover:border-gold/50 transition-colors duration-300">
              <UserPlus size={14} /> {t("register")}
            </button>
          </div>

          <div className="md:col-span-6">
            <form onSubmit={submit} data-testid="catalog-login-form"
              className="backdrop-blur-xl bg-black/60 border border-white/10 p-8 md:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.6)]">
              <div className="flex items-center gap-3 mb-8">
                <Lock size={16} className="text-gold" />
                <p className="text-[0.68rem] uppercase tracking-[0.24em] text-neutral-300">{t("login")}</p>
              </div>
              {["username", "password"].map((f) => (
                <div key={f} className="mb-6">
                  <label className="block text-[0.62rem] uppercase tracking-[0.22em] text-neutral-500 mb-2">{t(f)}</label>
                  <input required data-testid={`catalog-${f}`} type={f === "password" ? "password" : "text"}
                    value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                    className="w-full bg-[#131313] border border-white/10 px-3 py-3 text-sm text-white outline-none focus:border-gold transition-colors duration-300" />
                </div>
              ))}
              {error && <p data-testid="catalog-login-error" className="text-xs text-red-400 mb-4">{error}</p>}
              <button type="submit" disabled={busy} data-testid="catalog-login-btn"
                className="w-full bg-gold text-black py-3.5 text-[0.72rem] uppercase tracking-[0.24em] font-medium hover:bg-white transition-colors duration-300 disabled:opacity-50">
                {busy ? "..." : t("login")}
              </button>
            </form>
          </div>
        </div>
        <RegisterDialog open={regOpen} onOpenChange={setRegOpen} />
      </div>
    );
  }

  return (
    <div data-testid="catalog-page" className="px-6 md:px-12 py-20 md:py-28">
      <div className="max-w-[1600px] mx-auto">
        <p className="overline">Private Access · {user.username}</p>
        <h1 className="font-serif text-4xl sm:text-6xl text-white mt-4">{t("catalog")}</h1>
        <div className="gold-line mt-8 w-40" />

        {items === null ? (
          <p className="mt-16 text-sm text-neutral-500">{t("loading")}</p>
        ) : items.length === 0 ? (
          <p data-testid="catalog-empty" className="mt-16 text-sm text-neutral-500">{t("noItems")}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {items.map((c, i) => (
              <div key={c.id} data-testid={`catalog-item-${i}`} className="group border border-white/5 bg-[#0c0c0c]">
                <div className="aspect-[3/4] overflow-hidden bg-black flex items-center justify-center">
                  {c.cover_url ? (
                    <img src={mediaUrl(c.cover_url)} alt={c.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : <FileText size={40} className="text-neutral-700" />}
                </div>
                <div className="p-6">
                  <p className="text-[0.6rem] uppercase tracking-[0.22em] text-gold">{c.year}</p>
                  <h3 className="font-serif text-2xl text-white mt-2">{c.title}</h3>
                  {c.description && <p className="text-sm text-neutral-500 mt-2">{c.description}</p>}
                  {c.file_url && (
                    <a href={mediaUrl(c.file_url)} target="_blank" rel="noreferrer"
                      data-testid={`catalog-download-${i}`}
                      className="inline-block mt-5 border border-gold/40 text-gold px-5 py-2 text-[0.66rem] uppercase tracking-[0.2em] hover:bg-gold hover:text-black transition-colors duration-300">
                      {t("download")}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
